import { Loader2 } from "lucide-react";
import { useEffect, useId, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { AddressPickerSavedList } from "@/components/shared/address-picker-saved-list";
import { listUserAddresses, type UserAddressCreatePayload } from "@/services/userAddressesService";
import { AddressMapPreview } from "@/components/shared/AddressMapPreview";
import { loadGoogleMapsScript } from "@/utils/loadGoogleMapsScript";
import { googlePlaceClassToPayload } from "@/utils/googlePlaceToAddressPayload";

export type AddressSelection =
  | {
      kind: "saved";
      addressId: string;
      /** From address book; used for pickup/delivery validation and API. */
      contactPhone?: string | null;
      contactPersonName?: string | null;
      /** Courier instructions stored on the address (delivery flows). */
      courierNotes?: string | null;
    }
  | { kind: "new"; payload: UserAddressCreatePayload }
  | null;

export type AddressSelectionReadyOptions = {
  minContactPhoneLength?: number;
};

/**
 * Drop the Places claim when the user edits what that claim describes.
 *
 * WHY: picking a STREET-level suggestion and then typing the number by hand
 * used to keep `googlePlaceId` and the coordinates pointing at the street,
 * while the text fields said the building. Nothing re-geocodes and nothing
 * validated the pair, so the address asserted a precision it did not have —
 * which is why the same address entered "in one line" and "in parts" rendered
 * visibly different map pins, the symptom reported on request fdbc73a5.
 *
 * Clearing rather than re-geocoding is deliberate. The only consumer of these
 * coordinates is the map preview: nothing routes on them, and the courier
 * integration they were added for is gone (the professional ships directly,
 * decision 2a18572). A billed Places call per edit to improve a picture is not
 * worth it; showing no pin is honest where showing a confidently wrong one is
 * the actual defect.
 *
 * ONLY for fields the place asserts — route, streetNumber, city, adminArea,
 * formattedAddress. `label`, `addressLine2` (the apartment), `contactPhone`,
 * `contactPersonName` and `courierNotes` say nothing about where the building
 * is and must keep the place intact.
 *
 * SAFE AGAINST HIDING THE FORM MID-EDIT: `showNewAddressDetailFields` falls
 * back to `isPayloadComplete`, which a Places pick always satisfies through
 * `formattedAddress` (googlePlaceToAddressPayload sets it unconditionally), so
 * the fields stay rendered once the place id is gone.
 */
function withoutPlaceClaim(prev: UserAddressCreatePayload): UserAddressCreatePayload {
  if (prev.googlePlaceId == null && prev.latitude == null && prev.longitude == null) return prev;
  const { googlePlaceId: _placeId, latitude: _lat, longitude: _lng, ...rest } = prev;
  return rest;
}

function isPayloadComplete(p: UserAddressCreatePayload): boolean {
  const hasPlace = Boolean(p.googlePlaceId?.trim());
  const hasFormatted = Boolean(p.formattedAddress?.trim());
  const hasStructured = Boolean(
    p.route?.trim() ||
      p.city?.trim() ||
      p.neighborhood?.trim() ||
      p.adminArea?.trim(),
  );
  return hasPlace || hasFormatted || hasStructured;
}

/**
 * Uruguay's nineteen departments, for SUGGESTION only.
 *
 * Display names, and deliberately not a second source of truth: what is
 * submitted is whatever the field holds, and the server resolves it through
 * uy-departments.ts, which forgives case, accents and the "Departamento de X"
 * forms. So this list drifting would cost a suggestion, never a match.
 */
const UY_DEPARTMENT_NAMES = [
  "Artigas",
  "Canelones",
  "Cerro Largo",
  "Colonia",
  "Durazno",
  "Flores",
  "Florida",
  "Lavalleja",
  "Maldonado",
  "Montevideo",
  "Paysandú",
  "Río Negro",
  "Rivera",
  "Rocha",
  "Salto",
  "San José",
  "Soriano",
  "Tacuarembó",
  "Treinta y Tres",
] as const;

type PlaceAutocompleteLike = HTMLElement & {
  includedRegionCodes?: string[];
  placeholder?: string;
};

function isPlaceAutocompleteCtor(
  x: unknown,
): x is new (opts?: object) => PlaceAutocompleteLike {
  return typeof x === "function";
}

export function AddressPicker({
  selection,
  onSelectionChange,
  disabled = false,
  /** When true, offers "save to address book" for newly entered addresses (parent handles API). */
  showSaveCheckbox = false,
  saveToBook = true,
  onSaveToBookChange,
  /** Adds label + contact phone / person for pickup or delivery (stored on the address). */
  showContactFields = false,
  /** Courier / delivery instructions on the “New address” tab only (saved cards already show notes; edit them in the address editor). */
  showCourierNotesField = false,
  idPrefix: idPrefixProp,
}: {
  selection: AddressSelection;
  onSelectionChange: (next: AddressSelection) => void;
  disabled?: boolean;
  showSaveCheckbox?: boolean;
  saveToBook?: boolean;
  onSaveToBookChange?: (v: boolean) => void;
  showContactFields?: boolean;
  showCourierNotesField?: boolean;
  idPrefix?: string;
}): JSX.Element {
  const { t, i18n } = useTranslation();
  const reactId = useId();
  const idPrefix = idPrefixProp ?? `addr-${reactId}`;
  /** Default to saved tab; effect switches to “new” when there are no saved addresses. */
  const [mode, setMode] = useState<"saved" | "new">("saved");
  const [autocompleteReady, setAutocompleteReady] = useState(false);
  const autocompleteHostRef = useRef<HTMLDivElement>(null);
  const pacRef = useRef<PlaceAutocompleteLike | null>(null);
  const onSelectionChangeRef = useRef(onSelectionChange);
  onSelectionChangeRef.current = onSelectionChange;
  const selectionRef = useRef(selection);
  selectionRef.current = selection;

  const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY?.trim() ?? "";
  /**
   * Places could not be used, either because loading it threw or because the
   * professional/consumer gave up on it and asked to type the address.
   *
   * SEPARATE from `autocompleteReady`, which is "not ready YET" and is the
   * normal state for the first few hundred milliseconds. This one is "not
   * happening", and it is what reveals manual entry.
   */
  const [addressSearchUnavailable, setAddressSearchUnavailable] = useState(false);
  /** The user asked to type it in, whatever Places is doing. */
  const [manualEntryRequested, setManualEntryRequested] = useState(false);
  /**
   * A suggestion was picked and its details are being fetched.
   *
   * THE SECOND WAIT, and the one nothing showed. Picking a suggestion does not
   * hand over an address — `place.fetchFields` is another round trip before
   * the form can be filled, and until it lands the screen is unchanged: the
   * dropdown has closed, the fields below are still empty, and nothing says
   * why. On a slow connection that reads as the click not having registered,
   * so people pick again.
   *
   * Distinct from `autocompleteReady` (the library is still loading, before
   * anyone has typed) — the two happen at opposite ends of the interaction and
   * say different things.
   */
  const [resolvingPlace, setResolvingPlace] = useState(false);
  /** fetchFields rejected — the pick produced nothing and the user must retry. */
  const [placeLookupFailed, setPlaceLookupFailed] = useState(false);

  const { data: addressesData } = useQuery({
    queryKey: ["me", "addresses"],
    queryFn: listUserAddresses,
  });
  const addresses = addressesData?.items ?? [];

  useEffect(() => {
    if (addressesData == null) return;
    const items = addressesData.items ?? [];

    if (items.length === 0) {
      setMode("new");
      if (selection?.kind === "saved") {
        onSelectionChange(null);
      }
      return;
    }

    if (selection?.kind === "new") {
      return;
    }

    setMode("saved");

    const selectedId = selection?.kind === "saved" ? selection.addressId : undefined;
    if (selectedId && items.some((a) => a.id === selectedId)) {
      const row = items.find((a) => a.id === selectedId)!;
      const cur = selection?.kind === "saved" ? selection : null;
      if (
        cur?.addressId === selectedId &&
        (cur.contactPhone ?? "") === (row.contactPhone ?? "") &&
        (cur.contactPersonName ?? "") === (row.contactPersonName ?? "") &&
        (cur.courierNotes ?? "") === (row.courierNotes ?? "")
      ) {
        return;
      }
      onSelectionChange({
        kind: "saved",
        addressId: selectedId,
        contactPhone: row.contactPhone ?? undefined,
        contactPersonName: row.contactPersonName ?? undefined,
        courierNotes: row.courierNotes ?? undefined,
      });
      return;
    }

    const pick = items.find((a) => a.isDefault) ?? items[0];
    onSelectionChange({
      kind: "saved",
      addressId: pick.id,
      contactPhone: pick.contactPhone ?? undefined,
      contactPersonName: pick.contactPersonName ?? undefined,
      courierNotes: pick.courierNotes ?? undefined,
    });
  }, [addressesData, selection, onSelectionChange]);

  useEffect(() => {
    if (mode !== "new" || !apiKey || disabled) {
      setAutocompleteReady(false);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        await loadGoogleMapsScript(apiKey);
        await google.maps.importLibrary("places");
        if (!cancelled) setAutocompleteReady(true);
      } catch {
        // The ONE Places failure that is observable from JS. A key rejected
        // for its HTTP referrer, or a project without Places enabled, does
        // NOT land here — the Maps API reports those to the console and the
        // element simply returns nothing. That is why the manual escape below
        // is user-driven rather than relying on this.
        if (!cancelled) {
          setAutocompleteReady(false);
          setAddressSearchUnavailable(true);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [mode, apiKey, disabled]);

  useEffect(() => {
    if (!autocompleteReady || mode !== "new" || !apiKey || disabled) {
      return;
    }
    const host = autocompleteHostRef.current;
    const PlaceCtor = google.maps.places.PlaceAutocompleteElement;
    if (!host || !isPlaceAutocompleteCtor(PlaceCtor)) {
      return;
    }

    host.replaceChildren();
    const pac = new PlaceCtor({}) as PlaceAutocompleteLike;
    pac.includedRegionCodes = ["uy"];
    pac.placeholder = t("addressPicker.searchPlaceholder");
    /* Avoid bg-background here: Places UI Kit uses --gmp-mat-color-surface inside shadow DOM;
       a mismatched OS dark scheme caused invisible text on a light host (see index.css). */
    pac.className =
      "flex h-10 w-full rounded-md border border-input text-sm ring-offset-background transition-colors duration-150 motion-reduce:transition-none";

    const onSelect = async (ev: Event): Promise<void> => {
      const withPred = ev as Event & {
        placePrediction?: { toPlace: () => google.maps.places.Place };
      };
      const fromDetail = (ev as CustomEvent<{ placePrediction?: { toPlace: () => google.maps.places.Place } }>)
        .detail?.placePrediction;
      const prediction = withPred.placePrediction ?? fromDetail;
      if (!prediction) return;
      const place = prediction.toPlace();
      setPlaceLookupFailed(false);
      setResolvingPlace(true);
      try {
        await place.fetchFields({
          fields: ["id", "formattedAddress", "location", "addressComponents"],
        });
        /*
          NO LOCATION IS A FAILURE, not a quiet no-op. It used to `return`,
          which left the form exactly as it was and told the consumer nothing —
          the same dead end as a rejected fetch, reached by a different route.
        */
        if (!place.location) {
          setPlaceLookupFailed(true);
          return;
        }
        const prevPayload =
          selectionRef.current?.kind === "new" ? selectionRef.current.payload : {};
        const placePayload = googlePlaceClassToPayload(place);
        onSelectionChangeRef.current({
          kind: "new",
          payload: {
            ...placePayload,
            countryCode: placePayload.countryCode ?? prevPayload.countryCode ?? "UY",
            label: prevPayload.label,
            contactPhone: prevPayload.contactPhone,
            contactPersonName: prevPayload.contactPersonName,
            courierNotes: prevPayload.courierNotes,
          },
        });
      } catch {
        /*
          `void onSelect(e)` is the listener, so a rejection here had nowhere to
          go — it surfaced as an unhandled rejection in the console and as
          nothing at all on screen. The manual-entry escape below is the way
          forward, and it is already always on offer.
        */
        setPlaceLookupFailed(true);
      } finally {
        setResolvingPlace(false);
      }
    };

    pac.addEventListener("gmp-select", (e) => void onSelect(e));
    host.appendChild(pac);
    pacRef.current = pac;

    return () => {
      pacRef.current = null;
      host.replaceChildren();
    };
  }, [autocompleteReady, mode, apiKey, disabled, t, i18n.language]);

  useEffect(() => {
    const el = pacRef.current;
    if (!el) return;
    if (disabled) {
      el.setAttribute("disabled", "");
    } else {
      el.removeAttribute("disabled");
    }
  }, [disabled, autocompleteReady, mode]);

  const handleModeSaved = (): void => {
    setMode("saved");
    const pick = addresses.find((a) => a.isDefault) ?? addresses[0];
    if (pick) {
      onSelectionChange({
        kind: "saved",
        addressId: pick.id,
        contactPhone: pick.contactPhone ?? undefined,
        contactPersonName: pick.contactPersonName ?? undefined,
        courierNotes: pick.courierNotes ?? undefined,
      });
    } else {
      onSelectionChange(null);
    }
  };

  const handleModeNew = (): void => {
    setMode("new");
    onSelectionChange({ kind: "new", payload: { countryCode: "UY" } });
  };

  /**
   * When the editable address fields are shown.
   *
   * With Places working, only after a selection — the fields are prefilled
   * from the place and editing them before that is editing nothing.
   *
   * WITHOUT it, immediately, and there are now three ways to be without it:
   * no API key at all, a load failure, or the person telling us the search is
   * not finding their address. The third exists because the most common Places
   * failures — a key restricted to the wrong referrer, a project without
   * Places enabled — are invisible to JS: the element mounts, accepts typing,
   * and silently returns nothing. Waiting to DETECT that would mean waiting
   * forever, so the escape hatch is theirs to pull.
   *
   * This matters beyond convenience: the RFQ delivery step gates submission on
   * having an address, so with no manual path a Places outage stops consumers
   * completing any request that produces a physical document.
   */
  const addressSearchUsable = Boolean(apiKey) && !addressSearchUnavailable && !manualEntryRequested;
  const showNewAddressDetailFields =
    !addressSearchUsable ||
    (selection?.kind === "new" &&
      (Boolean(selection.payload.googlePlaceId?.trim()) || isPayloadComplete(selection.payload)));

  const newPayload = selection?.kind === "new" ? selection.payload : null;
  const mapLat = newPayload?.latitude;
  const mapLng = newPayload?.longitude;
  const showAddressMapPreview =
    addressSearchUsable &&
    showNewAddressDetailFields &&
    typeof mapLat === "number" &&
    typeof mapLng === "number" &&
    Number.isFinite(mapLat) &&
    Number.isFinite(mapLng);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          disabled={disabled || addresses.length === 0}
          onClick={handleModeSaved}
          className={`rounded-md border px-3 py-1.5 text-sm transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
            mode === "saved"
              ? "border-primary bg-primary/10 text-foreground"
              : "border-border bg-background text-muted-foreground hover:text-foreground"
          } disabled:opacity-50`}
        >
          {t("addressPicker.savedTab")}
        </button>
        <button
          type="button"
          disabled={disabled}
          onClick={handleModeNew}
          className={`rounded-md border px-3 py-1.5 text-sm transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
            mode === "new"
              ? "border-primary bg-primary/10 text-foreground"
              : "border-border bg-background text-muted-foreground hover:text-foreground"
          }`}
        >
          {t("addressPicker.newTab")}
        </button>
      </div>

      {mode === "saved" ? (
        <div className="space-y-4">
          <AddressPickerSavedList
            addresses={addresses}
            selectedId={selection?.kind === "saved" ? selection.addressId : undefined}
            onSelect={(row) =>
              onSelectionChange({
                kind: "saved",
                addressId: row.id,
                contactPhone: row.contactPhone ?? undefined,
                contactPersonName: row.contactPersonName ?? undefined,
                courierNotes: row.courierNotes ?? undefined,
              })
            }
            disabled={disabled || addresses.length === 0}
            idPrefix={idPrefix}
          />
        </div>
      ) : (
        <div className="space-y-3">
          {/* Says WHY the fields are here when search was meant to fill them.
              Without this the form simply looks different from the one in the
              wizard, and a consumer cannot tell whether something is broken or
              whether they did something. */}
          {!addressSearchUsable && (apiKey || manualEntryRequested) ? (
            <p className="rounded-md border border-border bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
              {t(
                addressSearchUnavailable
                  ? "addressPicker.searchUnavailable"
                  : "addressPicker.manualEntryActive",
              )}
            </p>
          ) : null}
          {addressSearchUsable ? (
            <div className="space-y-2">
              <Label htmlFor={`${idPrefix}-search`}>{t("addressPicker.searchLabel")}</Label>
              <div id={`${idPrefix}-search`} ref={autocompleteHostRef} className="w-full" />
              {/*
                A FIELD-SHAPED PLACEHOLDER, not a line of text under a gap.

                The host div above is EMPTY until Places loads — the web
                component is appended to it — so the old treatment left the
                label sitting over nothing with "cargando" beneath it, which
                reads as a form that failed to render rather than one still
                arriving. This occupies the same h-10 box with the same border
                and radius the real control gets, so the layout does not jump
                when it swaps in.
              */}
              {!autocompleteReady ? (
                <div
                  className="flex h-10 w-full items-center gap-2 rounded-md border border-input bg-muted/30 px-3 text-sm text-muted-foreground"
                  role="status"
                  aria-live="polite"
                >
                  <Loader2 className="h-4 w-4 animate-spin motion-reduce:animate-none" aria-hidden />
                  <span>{t("addressPicker.mapsLoading")}</span>
                </div>
              ) : null}
              {/*
                The pick landed and its details are in flight. Replaces the
                "choose a suggestion" hint rather than stacking under it —
                they answer the same question and only one is true at a time.
              */}
              {resolvingPlace ? (
                <p
                  className="flex items-center gap-2 text-xs text-muted-foreground"
                  role="status"
                  aria-live="polite"
                >
                  <Loader2 className="h-3.5 w-3.5 animate-spin motion-reduce:animate-none" aria-hidden />
                  {t("addressPicker.resolvingPlace")}
                </p>
              ) : null}
              {placeLookupFailed ? (
                <p className="text-xs text-destructive">{t("addressPicker.placeLookupFailed")}</p>
              ) : null}
              {autocompleteReady && !resolvingPlace && !showNewAddressDetailFields ? (
                <p className="text-xs text-muted-foreground">{t("addressPicker.selectPlaceHint")}</p>
              ) : null}
              {/* THE ESCAPE HATCH, and it is always offered rather than shown
                  only on a detected failure. The common Places failures — a
                  key restricted to the wrong referrer, a project without
                  Places enabled — are invisible to JS: the box accepts typing
                  and silently returns nothing. Someone staring at that has no
                  way forward, and on the RFQ delivery step no way to submit
                  at all. One line of text costs nothing when search works. */}
              {autocompleteReady ? (
                <button
                  type="button"
                  className="text-xs text-muted-foreground underline underline-offset-2 transition-colors duration-150 hover:text-foreground"
                  onClick={() => setManualEntryRequested(true)}
                  disabled={disabled}
                >
                  {t("addressPicker.enterManually")}
                </button>
              ) : null}
            </div>
          ) : !apiKey ? (
            // ONLY when the key is genuinely absent. This branch now also
            // covers a load failure and the user choosing manual entry, and in
            // both of those the key is fine — blaming it sends whoever reads
            // this to fix the wrong thing. It also named an internal env var
            // to consumers, which is developer-facing text on a customer
            // screen; the message above explains the state in their terms.
            <p className="text-xs text-amber-700 dark:text-amber-400">{t("addressPicker.noApiKeyHint")}</p>
          ) : null}

          {showNewAddressDetailFields ? (
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor={`${idPrefix}-label`}>{t("addressPicker.labelField")}</Label>
              <Input
                id={`${idPrefix}-label`}
                disabled={disabled}
                value={selection?.kind === "new" ? (selection.payload.label ?? "") : ""}
                onChange={(e) => {
                  const prev = selection?.kind === "new" ? selection.payload : {};
                  onSelectionChange({
                    kind: "new",
                    payload: {
                      ...prev,
                      label: e.target.value || undefined,
                      countryCode: prev.countryCode ?? "UY",
                    },
                  });
                }}
                placeholder={t("addressPicker.labelPlaceholder")}
                className="transition-colors duration-150"
              />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor={`${idPrefix}-formatted`}>{t("addressPicker.formattedLabel")}</Label>
              <Input
                id={`${idPrefix}-formatted`}
                disabled={disabled}
                value={selection?.kind === "new" ? (selection.payload.formattedAddress ?? "") : ""}
                onChange={(e) => {
                  const v = e.target.value;
                  const prev = selection?.kind === "new" ? selection.payload : {};
                  onSelectionChange({
                    kind: "new",
                    payload: { ...withoutPlaceClaim(prev), formattedAddress: v || undefined, countryCode: prev.countryCode ?? "UY" },
                  });
                }}
                placeholder={t("addressPicker.formattedPlaceholder")}
                className="transition-colors duration-150"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor={`${idPrefix}-route`}>{t("addressPicker.routeLabel")}</Label>
              <Input
                id={`${idPrefix}-route`}
                disabled={disabled}
                value={selection?.kind === "new" ? (selection.payload.route ?? "") : ""}
                onChange={(e) => {
                  const prev = selection?.kind === "new" ? selection.payload : {};
                  onSelectionChange({
                    kind: "new",
                    payload: { ...withoutPlaceClaim(prev), route: e.target.value || undefined, countryCode: prev.countryCode ?? "UY" },
                  });
                }}
                className="transition-colors duration-150"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor={`${idPrefix}-street`}>{t("addressPicker.streetNumberLabel")}</Label>
              <Input
                id={`${idPrefix}-street`}
                disabled={disabled}
                value={selection?.kind === "new" ? (selection.payload.streetNumber ?? "") : ""}
                onChange={(e) => {
                  const prev = selection?.kind === "new" ? selection.payload : {};
                  onSelectionChange({
                    kind: "new",
                    payload: {
                      ...withoutPlaceClaim(prev),
                      streetNumber: e.target.value || undefined,
                      countryCode: prev.countryCode ?? "UY",
                    },
                  });
                }}
                className="transition-colors duration-150"
              />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor={`${idPrefix}-line2`}>{t("addressPicker.addressLine2Label")}</Label>
              <Input
                id={`${idPrefix}-line2`}
                disabled={disabled}
                value={selection?.kind === "new" ? (selection.payload.addressLine2 ?? "") : ""}
                onChange={(e) => {
                  const prev = selection?.kind === "new" ? selection.payload : {};
                  onSelectionChange({
                    kind: "new",
                    payload: {
                      ...prev,
                      addressLine2: e.target.value || undefined,
                      countryCode: prev.countryCode ?? "UY",
                    },
                  });
                }}
                placeholder={t("addressPicker.addressLine2Placeholder")}
                className="transition-colors duration-150"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor={`${idPrefix}-city`}>{t("addressPicker.cityLabel")}</Label>
              <Input
                id={`${idPrefix}-city`}
                disabled={disabled}
                value={selection?.kind === "new" ? (selection.payload.city ?? "") : ""}
                onChange={(e) => {
                  const prev = selection?.kind === "new" ? selection.payload : {};
                  onSelectionChange({
                    kind: "new",
                    payload: { ...withoutPlaceClaim(prev), city: e.target.value || undefined, countryCode: prev.countryCode ?? "UY" },
                  });
                }}
                className="transition-colors duration-150"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor={`${idPrefix}-dept`}>{t("addressPicker.adminAreaLabel")}</Label>
              {/* A datalist, not a select, and not left bare either.
                  Shipping is priced per DEPARTMENT, and the server resolves
                  this value against a closed list of nineteen — so a manually
                  typed "Ciudad de la Costa" sails past this field and then
                  blocks the delivery step with an address we cannot price.
                  Suggesting the nineteen steers typing without rejecting the
                  unusual input a free field exists to allow, and the server's
                  resolver already forgives case and accents. */}
              <datalist id={`${idPrefix}-dept-options`}>
                {UY_DEPARTMENT_NAMES.map((name) => (
                  <option key={name} value={name} />
                ))}
              </datalist>
              <Input
                id={`${idPrefix}-dept`}
                list={`${idPrefix}-dept-options`}
                disabled={disabled}
                value={selection?.kind === "new" ? (selection.payload.adminArea ?? "") : ""}
                onChange={(e) => {
                  const prev = selection?.kind === "new" ? selection.payload : {};
                  onSelectionChange({
                    kind: "new",
                    payload: {
                      ...withoutPlaceClaim(prev),
                      adminArea: e.target.value || undefined,
                      countryCode: prev.countryCode ?? "UY",
                    },
                  });
                }}
                className="transition-colors duration-150"
              />
            </div>
            {showContactFields ? (
              <>
                <div className="space-y-1.5 sm:col-span-2">
                  <Label htmlFor={`${idPrefix}-contact-phone`} className="text-foreground">
                    {t("addressPicker.contactPhoneLabel")}
                  </Label>
                  <Input
                    id={`${idPrefix}-contact-phone`}
                    type="tel"
                    autoComplete="tel"
                    disabled={disabled}
                    placeholder={t("addressPicker.contactPhonePlaceholder")}
                    value={selection?.kind === "new" ? (selection.payload.contactPhone ?? "") : ""}
                    onChange={(e) => {
                      const prev = selection?.kind === "new" ? selection.payload : {};
                      onSelectionChange({
                        kind: "new",
                        payload: {
                          ...prev,
                          contactPhone: e.target.value || undefined,
                          countryCode: prev.countryCode ?? "UY",
                        },
                      });
                    }}
                    className="transition-colors duration-150"
                  />
                </div>
                <div className="space-y-1.5 sm:col-span-2">
                  <Label htmlFor={`${idPrefix}-contact-person`} className="text-foreground">
                    {t("addressPicker.contactPersonLabel")}
                  </Label>
                  <Input
                    id={`${idPrefix}-contact-person`}
                    autoComplete="name"
                    disabled={disabled}
                    placeholder={t("addressPicker.contactPersonPlaceholder")}
                    value={selection?.kind === "new" ? (selection.payload.contactPersonName ?? "") : ""}
                    onChange={(e) => {
                      const prev = selection?.kind === "new" ? selection.payload : {};
                      onSelectionChange({
                        kind: "new",
                        payload: {
                          ...prev,
                          contactPersonName: e.target.value || undefined,
                          countryCode: prev.countryCode ?? "UY",
                        },
                      });
                    }}
                    className="transition-colors duration-150"
                  />
                </div>
              </>
            ) : null}
            {showCourierNotesField ? (
              <div className="space-y-1.5 sm:col-span-2">
                <Label htmlFor={`${idPrefix}-courier-notes-new`} className="text-foreground">
                  {t("requests.user.deliveryNotesLabel")}
                </Label>
                <Textarea
                  id={`${idPrefix}-courier-notes-new`}
                  rows={3}
                  disabled={disabled}
                  placeholder={t("requests.user.deliveryNotesPlaceholder")}
                  value={selection?.kind === "new" ? (selection.payload.courierNotes ?? "") : ""}
                  onChange={(e) => {
                    const prev = selection?.kind === "new" ? selection.payload : {};
                    onSelectionChange({
                      kind: "new",
                      payload: {
                        ...prev,
                        courierNotes: e.target.value || undefined,
                        countryCode: prev.countryCode ?? "UY",
                      },
                    });
                  }}
                  className="transition-colors duration-150"
                />
              </div>
            ) : null}
          </div>
          ) : null}

          {showAddressMapPreview ? (
            <AddressMapPreview
              className="pt-1"
              lat={mapLat}
              lng={mapLng}
              caption={t("addressPicker.mapPreviewCaption")}
              disableInteractions={disabled}
              mapsApiKey={apiKey}
            />
          ) : null}

          {showSaveCheckbox &&
          showNewAddressDetailFields &&
          selection?.kind === "new" &&
          isPayloadComplete(selection.payload) ? (
            <div className="flex w-full flex-row justify-end">
              <div className="flex items-center gap-2">
                <Checkbox
                  id={`${idPrefix}-save`}
                  checked={saveToBook}
                  disabled={disabled}
                  onCheckedChange={(v) => onSaveToBookChange?.(v === true)}
                />
                <Label htmlFor={`${idPrefix}-save`} className="text-sm font-normal text-foreground">
                  {t("addressPicker.saveToBook")}
                </Label>
              </div>
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}

/**
 * WHY a selection is not ready, or null when it is.
 *
 * Exists because the submit buttons that consult `isAddressSelectionReady`
 * were disabled with no explanation, and the only non-obvious requirement —
 * a contact phone — sits at the BOTTOM of a long form. A notary filled in a
 * complete address, found the button dead, and had nothing on screen telling
 * him what was missing. A disabled control that states no precondition is
 * indistinguishable from a broken one.
 */
export type AddressSelectionBlocker = "address" | "contactPhone";

export function addressSelectionBlocker(
  s: AddressSelection,
  opts?: AddressSelectionReadyOptions,
): AddressSelectionBlocker | null {
  if (!s) return "address";
  const minLen = opts?.minContactPhoneLength;
  if (s.kind === "saved") {
    if (!s.addressId) return "address";
    if (minLen == null) return null;
    return (s.contactPhone?.trim().length ?? 0) >= minLen ? null : "contactPhone";
  }
  if (!isPayloadComplete(s.payload)) return "address";
  if (minLen == null) return null;
  return (s.payload.contactPhone?.trim().length ?? 0) >= minLen ? null : "contactPhone";
}

export function isAddressSelectionReady(
  s: AddressSelection,
  opts?: AddressSelectionReadyOptions,
): boolean {
  // Delegates, so the predicate and the reason can never disagree about the
  // same selection — which is exactly how a button ends up disabled while the
  // hint beside it says everything is fine.
  return addressSelectionBlocker(s, opts) == null;
}
